export type FollowUpStatus = 'OPEN' | 'DONE';

/** Filter for the list endpoints (tabs on the overview page). */
export type FollowUpView = 'OPEN' | 'OVERDUE' | 'TODAY' | 'PLANNED' | 'DONE';

/** Section a list item belongs to, calculated by the backend. */
export type FollowUpGroup = 'OVERDUE' | 'TODAY' | 'TOMORROW' | 'NEXT_WEEK' | 'LATER' | 'DONE';

/** Our own keys – the backend stores the category as free text */
export type FollowUpCategory =
  // Vertrieb & Kundenkontakt
  | 'FOLLOW_UP'
  | 'CALLBACK'
  | 'BIRTHDAY'
  | 'CONSULTATION'
  | 'ANNUAL_REVIEW'
  // Verträge & Bestand
  | 'CONTRACT_EXPIRY'
  | 'TERMINATION'
  | 'PORTFOLIO_TRANSFER'
  | 'CONTRACT_CHANGE'
  | 'PREMIUM_ADJUSTMENT'
  // Service & Vorgänge
  | 'DOCUMENT_REQUEST'
  | 'CLAIM'
  | 'APPLICATION_STATUS'
  | 'PAYMENT_OPEN'
  | 'GENERAL';

export type FollowUpReminderOffset = 'AT_DUE' | 'HOUR_1' | 'DAY_1' | 'WEEK_1';

export type FollowUpChannel = 'IN_APP' | 'EMAIL';

export type FollowUpRepeatRule = 'NONE' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';

export type FollowUpSnoozeOption = 'HOUR_1' | 'TOMORROW' | 'NEXT_WEEK';

export type FollowUpSource = 'MANUAL' | 'CONTRACT_EXPIRY' | 'BIRTHDAY';

export type FollowUpHistoryType =
  'CREATED' | 'UPDATED' | 'REASSIGNED' | 'SNOOZED' | 'REMINDER_SENT' | 'DONE' | 'REOPENED';

export interface FollowUpLinkedEntity {
  type: string;
  id: string;
  summary: string | null;
  /** Client the linked entity belongs to (e.g. the contract owner) – missing on older follow-ups */
  clientId?: string | null;
}

export interface FollowUpClient {
  id: string;
  name: string;
  initials: string;
  profilePictureUrl?: string | null;
}

export interface FollowUpHistoryEntry {
  id: string;
  type: FollowUpHistoryType;
  actorId: string;
  payload: Record<string, unknown> | null;
  createdAt: string;
}

export interface FollowUpListItem {
  id: string;
  assigneeId: string;
  assigneeProfilePictureUrl?: string | null;
  title: string;
  note: string | null;
  dueDate: string;
  dueTime: string | null;
  category: FollowUpCategory;
  status: FollowUpStatus;
  doneAt: string | null;
  group: FollowUpGroup;
  clientIds: string[];
  clients: FollowUpClient[];
}

export interface FollowUpDetail {
  id: string;
  companyId: number;
  createdBy: string;
  assigneeId: string;
  assigneeProfilePictureUrl?: string | null;
  title: string;
  note: string | null;
  dueDate: string;
  dueTime: string | null;
  category: FollowUpCategory;
  status: FollowUpStatus;
  doneAt: string | null;
  reminderOffset: FollowUpReminderOffset;
  channels: FollowUpChannel[];
  repeatRule: FollowUpRepeatRule;
  linkedEntity: FollowUpLinkedEntity | null;
  source: FollowUpSource;
  createdAt: string;
  updatedAt: string;
  clients: FollowUpClient[];
  history: FollowUpHistoryEntry[];
}

export interface FollowUpPage {
  content: FollowUpListItem[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface FollowUpCounts {
  overdue: number;
  today: number;
  planned: number;
  doneThisWeek: number;
  doneTotal: number;
  /** overdue + today – used for the badges */
  due: number;
}

export interface FollowUpWriteRequest {
  title: string;
  note: string | null;
  clientIds: string[];
  dueDate: string;
  dueTime: string | null;
  category: FollowUpCategory;
  assigneeId: string;
  reminderOffset: FollowUpReminderOffset;
  channels: FollowUpChannel[];
  repeatRule: FollowUpRepeatRule;
  linkedEntity: FollowUpLinkedEntity | null;
}

export interface FollowUpListFilter {
  view?: FollowUpView;
  assigneeId?: string | null;
  category?: FollowUpCategory | null;
  clientId?: string | null;
  search?: string | null;
  page?: number;
  size?: number;
}

export interface FollowUpCategoryOption {
  key: FollowUpCategory;
  label: string;
  description: string;
}

export interface FollowUpCategoryGroup {
  label: string;
  categories: FollowUpCategoryOption[];
}

/** Grouped list for the "Kategorie" dropdown in the modal and the category filter */
export const FOLLOW_UP_CATEGORY_GROUPS: FollowUpCategoryGroup[] = [
  {
    label: 'Vertrieb & Kundenkontakt',
    categories: [
      { key: 'FOLLOW_UP', label: 'Nachfassen', description: 'Angebot, Anfrage oder offenen Kontakt nachverfolgen' },
      { key: 'CALLBACK', label: 'Rückruf', description: 'Kunden oder Versicherer zurückrufen' },
      { key: 'BIRTHDAY', label: 'Geburtstag', description: 'Kunden zum Geburtstag kontaktieren' },
      { key: 'CONSULTATION', label: 'Beratungstermin', description: 'Beratung vereinbaren oder vorbereiten' },
      {
        key: 'ANNUAL_REVIEW',
        label: 'Jahresgespräch',
        description: 'Regelmäßigen Bestands- und Bedarfscheck durchführen',
      },
    ],
  },
  {
    label: 'Verträge & Bestand',
    categories: [
      { key: 'CONTRACT_EXPIRY', label: 'Vertragsablauf', description: 'Vertragsende oder Verlängerung prüfen' },
      {
        key: 'TERMINATION',
        label: 'Kündigung',
        description: 'Kündigungsfrist oder Kündigungsbestätigung überwachen',
      },
      {
        key: 'PORTFOLIO_TRANSFER',
        label: 'Bestandsübertragung',
        description: 'Maklervollmacht und Übertragungsstatus nachhalten',
      },
      { key: 'CONTRACT_CHANGE', label: 'Vertragsänderung', description: 'Änderungsantrag oder Nachtrag kontrollieren' },
      {
        key: 'PREMIUM_ADJUSTMENT',
        label: 'Beitragsanpassung',
        description: 'Beitragsänderung prüfen und Kunden kontaktieren',
      },
    ],
  },
  {
    label: 'Service & Vorgänge',
    categories: [
      {
        key: 'DOCUMENT_REQUEST',
        label: 'Dokument anfordern',
        description: 'Fehlende Unterlagen bei Kunde oder Versicherer anfordern',
      },
      { key: 'CLAIM', label: 'Schadenfall', description: 'Schadenbearbeitung oder Regulierung nachverfolgen' },
      {
        key: 'APPLICATION_STATUS',
        label: 'Antragsstatus',
        description: 'Prüfen, ob ein Antrag policiert oder angenommen wurde',
      },
      { key: 'PAYMENT_OPEN', label: 'Zahlung offen', description: 'Beitragsrückstand oder ausstehende Zahlung klären' },
      { key: 'GENERAL', label: 'Allgemeine Wiedervorlage', description: 'Freie Aufgabe ohne spezifischen Vorgang' },
    ],
  },
];

export const FOLLOW_UP_CATEGORY_LABELS = Object.fromEntries(
  FOLLOW_UP_CATEGORY_GROUPS.flatMap((group: FollowUpCategoryGroup) =>
    group.categories.map((category: FollowUpCategoryOption) => [category.key, category.label]),
  ),
) as Record<FollowUpCategory, string>;

/** Badge colours – background, text; the text colour is also used for the dot in the modal */
export const FOLLOW_UP_CATEGORY_COLORS: Record<FollowUpCategory, { background: string; color: string }> = {
  FOLLOW_UP: { background: 'var(--green-200)', color: 'var(--green-500)' },
  CALLBACK: { background: 'var(--blue-100)', color: 'var(--blue-500)' },
  BIRTHDAY: { background: 'var(--orange-100)', color: 'var(--orange-500)' },
  CONSULTATION: { background: 'var(--purple-50)', color: 'var(--purple-500)' },
  ANNUAL_REVIEW: { background: 'var(--purple-100)', color: 'var(--purple-400)' },
  CONTRACT_EXPIRY: { background: 'var(--gray-150)', color: 'var(--dark-600)' },
  TERMINATION: { background: 'var(--red-100)', color: 'var(--red-400)' },
  PORTFOLIO_TRANSFER: { background: 'var(--blue-300)', color: 'var(--blue-800)' },
  CONTRACT_CHANGE: { background: 'var(--yellow-100)', color: 'var(--yellow-900)' },
  PREMIUM_ADJUSTMENT: { background: 'var(--orange-100)', color: 'var(--orange-500)' },
  DOCUMENT_REQUEST: { background: 'var(--yellow-500)', color: 'var(--black-100)' },
  CLAIM: { background: 'var(--red-100)', color: 'var(--red-400)' },
  APPLICATION_STATUS: { background: 'var(--green-200)', color: 'var(--green-800)' },
  PAYMENT_OPEN: { background: 'var(--orange-100)', color: 'var(--orange-500)' },
  GENERAL: { background: 'var(--gray-150)', color: 'var(--dark-600)' },
};

/** Inline style for a category badge – unknown categories stay grey */
export function getFollowUpCategoryStyle(category: string | null): { background: string; color: string } {
  return FOLLOW_UP_CATEGORY_COLORS[category as FollowUpCategory] ?? FOLLOW_UP_CATEGORY_COLORS.GENERAL;
}

/**
 * The backend creates automatic follow-ups with categories like "contract expiry" –
 * "Contract expiry", "contract-expiry" and "CONTRACT_EXPIRY" all become CONTRACT_EXPIRY.
 * Unknown categories are kept as they are.
 */
export function normalizeFollowUpCategory(category: string | null): FollowUpCategory {
  if (!category) {
    return category as FollowUpCategory;
  }

  const key: string = category
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  return (key in FOLLOW_UP_CATEGORY_LABELS ? key : category) as FollowUpCategory;
}

/** "Dominik Beratermann" → "DB" – fallback when there is no profile picture */
export function getFollowUpInitials(name: string | null): string {
  const parts: string[] = (name ?? '').trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return '';
  }

  return `${parts[0].charAt(0)}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase();
}

export const FOLLOW_UP_REMINDER_LABELS: Record<FollowUpReminderOffset, string> = {
  AT_DUE: 'Zum Fälligkeitszeitpunkt',
  HOUR_1: '1 Stunde vorher',
  DAY_1: '1 Tag vorher',
  WEEK_1: '1 Woche vorher',
};

export const FOLLOW_UP_CHANNEL_LABELS: Record<FollowUpChannel, string> = {
  IN_APP: 'Push an Kunden',
  EMAIL: 'E-Mail',
};

export const FOLLOW_UP_REPEAT_RULE_LABELS: Record<FollowUpRepeatRule, string> = {
  NONE: 'Nicht wiederholen',
  WEEKLY: 'Wöchentlich',
  MONTHLY: 'Monatlich',
  YEARLY: 'Jährlich',
};

// Websocket payloads for in-app reminders
export interface FollowUpReminder {
  id: string;
  title: string;
  dueDate: string;
  dueTime: string | null;
  clients: FollowUpClient[];
}

/** Topic `followup/due` – one follow-up just became due */
export interface FollowUpDueEvent {
  followUp: FollowUpReminder;
}

/** Topic `followup/due-digest` – several follow-ups became due at once */
export interface FollowUpDueDigestEvent {
  followUps: FollowUpReminder[];
}
